import { generateSlug } from './slugGenerator';
import { apiFetch } from '../../../core/utils/api';

// ---------------------------------------------------------------------------
// Static article parsing (unchanged)
// ---------------------------------------------------------------------------

// Function to parse article metadata and content from markdown text
export const parseArticle = (markdownText, filename) => {
  const lines = markdownText.split('\n');
  
  if (lines.length < 4) {
    throw new Error(`Invalid article format in ${filename}`);
  }
  
  const title = lines[0].trim();
  const author = lines[1].trim();
  const date = lines[2].trim();
  
  // Validate date format (YYYY-mm-dd)
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(date)) {
    throw new Error(`Invalid date format in ${filename}. Expected YYYY-mm-dd, got: ${date}`);
  }
  
  // Find the separator line "---"
  const separatorIndex = lines.findIndex((line, index) => index >= 3 && line.trim() === '---');
  if (separatorIndex === -1) {
    throw new Error(`Missing separator "---" in ${filename}`);
  }
  
  // Extract content after the separator
  const content = lines.slice(separatorIndex + 1).join('\n').trim();
  
  const slug = generateSlug(title, author, date);
  
  return {
    title,
    author,
    date,
    content,
    slug,
    filename,
    source: 'static',
  };
};

// ---------------------------------------------------------------------------
// Load static articles from bundled .md.js files
// ---------------------------------------------------------------------------

export const loadStaticArticles = async () => {
  const articles = [];

  const articleFiles = [
    'getting-started-with-portfolio-creation.md.js',
    'advanced-web-design-tips.md.js',
    'the-future-of-personal-branding.md.js'
  ];

  for (const filename of articleFiles) {
    try {
      const module = await import(`../articles/${filename}`);
      const markdownText = module.default;
      const article = parseArticle(markdownText, filename);
      articles.push(article);
    } catch (error) {
      console.warn(`Failed to load article ${filename}:`, error);
    }
  }

  return articles;
};

// ---------------------------------------------------------------------------
// Fetch published DB articles + hidden-static slugs from the API
// ---------------------------------------------------------------------------

const fetchPublishedDbArticles = async () => {
  try {
    const response = await apiFetch('/api/blog/', {
      credentials: 'include',
      suppressUnauthorizedEvent: true,
    });
    if (response.ok) {
      const data = await response.json();
      return (data?.results || []).map((post) => ({
        ...post,
        date: post.created_at ? post.created_at.slice(0, 10) : '',
        source: 'db',
      }));
    }
  } catch (err) {
    console.warn('Failed to fetch DB blog posts:', err);
  }
  return [];
};

const fetchHiddenStaticSlugs = async () => {
  try {
    const response = await apiFetch('/api/blog/hidden-static-slugs/', {
      credentials: 'include',
      suppressUnauthorizedEvent: true,
    });
    if (response.ok) {
      const data = await response.json();
      return new Set(data?.slugs || []);
    }
  } catch (err) {
    console.warn('Failed to fetch hidden static slugs:', err);
  }
  return new Set();
};

// ---------------------------------------------------------------------------
// Public loaders (used by Blog.js / BlogArticle.js)
// ---------------------------------------------------------------------------

/**
 * Load ALL visible articles (static + DB), filtering out hidden static ones,
 * sorted by date descending.
 */
export const loadAllArticles = async () => {
  const [staticArticles, dbArticles, hiddenSlugs] = await Promise.all([
    loadStaticArticles(),
    fetchPublishedDbArticles(),
    fetchHiddenStaticSlugs(),
  ]);

  // Filter out hidden static articles
  const visibleStatic = staticArticles.filter((a) => !hiddenSlugs.has(a.slug));

  // Merge and sort newest first
  const allArticles = [...visibleStatic, ...dbArticles];
  return allArticles.sort((a, b) => new Date(b.date) - new Date(a.date));
};

/**
 * Load a specific article by slug. Checks static articles first, then DB.
 */
export const loadArticleBySlug = async (slug) => {
  // Check static articles first (fast, no network)
  const staticArticles = await loadStaticArticles();
  const staticMatch = staticArticles.find((a) => a.slug === slug);
  if (staticMatch) {
    // Verify it's not hidden
    const hiddenSlugs = await fetchHiddenStaticSlugs();
    if (!hiddenSlugs.has(slug)) return staticMatch;
  }

  // Try DB
  try {
    const response = await apiFetch(`/api/blog/${slug}/`, {
      credentials: 'include',
      suppressUnauthorizedEvent: true,
    });
    if (response.ok) {
      const post = await response.json();
      return {
        ...post,
        date: post.created_at ? post.created_at.slice(0, 10) : '',
        source: 'db',
      };
    }
  } catch (err) {
    console.warn('Failed to fetch blog post by slug:', err);
  }

  return null;
};

/**
 * Get the next article relative to the current slug.
 */
export const getNextArticle = async (currentSlug) => {
  const allArticles = await loadAllArticles();
  const currentIndex = allArticles.findIndex(article => article.slug === currentSlug);
  
  if (currentIndex === -1) {
    return null;
  }
  
  // If it's the last article, return the first one (cycle back)
  if (currentIndex === allArticles.length - 1) {
    return allArticles[0];
  }
  
  return allArticles[currentIndex + 1];
};
